import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BridgeMode } from '@hg-com/xwc-js-bridge';
import { I18NextService } from 'angular-i18next';
import { Subscription } from 'rxjs';
import { generateCommunityToken } from 'src/core/services/b2b.util';
import { NativeIntegrationService } from 'src/core/services/native-integration.service';
import { environment } from 'src/environments/environment';
import { CartItem } from 'src/modules/cart/interfaces/cart.interface';

@Injectable({
  providedIn: 'root',
})
export class B2BService implements OnDestroy {
  apiUrl = environment.apiBaseUrl;
  contextNative = false;
  subscription!: Subscription;
  online = true;
  constructor(
    private httpClient: HttpClient,
    protected nativeIntegrationService: NativeIntegrationService,
    private i18next: I18NextService
  ) {
    this.initRoute().then();
    this.subscription = nativeIntegrationService.online$.subscribe((online) => {
      if (online) {
        this.online = true;
      } else {
        this.online = false;
      }
    });
  }

  async hasB2BAccess() {
    await this.nativeIntegrationService.spinTillReady();
    let token;
    if (this.nativeIntegrationService.native.mode > BridgeMode.Mock) {
      token = (
        await this.nativeIntegrationService.storage.secureStoreLoad$(
          'hasB2BAccess',
          { useBiometrics: false, usePinCode: 'false' }
        )
      ).data?.raw;
    } else {
      token = localStorage.getItem('hasB2BAccess');
    }
    return token === 'true';
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private throwIfOffline() {
    if (!this.online) {
      throw {
        text: this.i18next.t('cart.b2b.offline'),
        title: this.i18next.t('cart.b2b.offline-title'),
      };
    }
  }

  private async throwIfNoB2BAccess() {
    const hasAccess = await this.hasB2BAccess();
    if (!hasAccess) {
      throw {
        text: this.i18next.t('cart.b2b.noaccess'),
        title: this.i18next.t('cart.b2b.noaccess-title'),
      };
    }
  }

  public initRoute(): Promise<any> {
    return this.nativeIntegrationService.spinTillReady().then(() => {
      if (this.nativeIntegrationService.native.mode > BridgeMode.Mock) {
        this.apiUrl = `http://localhost:${this.nativeIntegrationService.native.port}/xwc/atum`;
        this.contextNative = true;
        console.log('[AUTH SERVICE]: Init Route native -> ' + this.apiUrl);
      } else {
        this.apiUrl = environment.apiBaseUrl;
        console.log('[AUTH SERVICE]: Init Route web version -> ' + this.apiUrl);
      }
    });
  }

  public async getToken(username: string, password: string) {
    this.throwIfOffline();
    const result = (await this.httpClient
      .post(`${this.apiUrl}/api/v1/b2b/token`, {
        username,
        password,
      })
      .toPromise()) as Promise<{
      email: string;
      sessionId: string;
      userId: string;
    }>;
    return result;
  }

  async checkProductsAvailable(items: CartItem[], sessionId: string) {
    this.throwIfOffline();
    await this.throwIfNoB2BAccess();
    const result = (await this.httpClient
      .post(`${this.apiUrl}/api/v1/b2b/check`, {
        items: items.map((x) => ({
          qty: x.quantity,
          sku: x.product.sku.toString(),
        })),
        token: sessionId,
      })
      .toPromise()) as {
      success: boolean;
      skuList: string[];
      message: string;
      cartId: number | null;
    };
    console.log(result);
    return result;
  }

  async addToCart(items: CartItem[], sessionId: string) {
    this.throwIfOffline();
    await this.throwIfNoB2BAccess();
    const result = (await this.httpClient
      .post(`${this.apiUrl}/api/v1/b2b/cart-add`, {
        items: items.map((x) => ({
          qty: x.quantity,
          sku: x.product.sku.toString(),
        })),
        token: sessionId,
      })
      .toPromise()) as {
      success: boolean;
      skuList: string[];
      message: string;
      cartId: number | null;
    };
    console.log(result);
    return result;
  }

  async createToCart(items: CartItem[], sessionId: string) {
    this.throwIfOffline();
    await this.throwIfNoB2BAccess();
    const result = (await this.httpClient
      .post(`${this.apiUrl}/api/v1/b2b/cart-create`, {
        items: items.map((x) => ({
          qty: x.quantity,
          sku: x.product.sku.toString(),
        })),
        token: sessionId,
      })
      .toPromise()) as {
      success: boolean;
      skuList: string[];
      message: string;
      cartId: number | null;
    };
    console.log(result);
    return result;
  }

  async getCommunityPw(sid: string, username: string, password: string) {
    this.throwIfOffline();
    const key = await this.httpClient
      .post(
        `${this.apiUrl}/api/v1/b2b/getKey`,
        { token: sid },
        { responseType: 'text' }
      )
      .toPromise();
    const token = await generateCommunityToken(sid, key, password);
    try {
      const ositResponse = await this.httpClient
        .post(
          `${this.apiUrl}/api/v1/b2b/getOneTimeToken`,
          {
            username,
            password: token,
            bt: sid,
          },
          { responseType: 'text', observe: 'response' }
        )
        .toPromise();
      return { osit: ositResponse.body!, sid, username };
    } catch (e) {
      console.error(e);
      // @ts-ignore
      if (e.status === 428) {
        return null;
      }
    }
    return null;
  }
}
